import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bar} from 'react-chartjs-2';
import { fetchAllStarRatings } from '../../apis/index';
import { getAllChatHistoryCached } from '../../actions/dataConsoleAction';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import DashLoader from '../../components/DashLoader';
import "../../styles/Dashboard.css";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar } from '@fortawesome/free-solid-svg-icons';


// Register the plugins for Chart.js
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, ArcElement, ChartDataLabels);

const Dashboard = () => {
  // Shared chat history fetched once for all sub-charts (Redux-cached across dashboards)
  const dispatch = useDispatch();
  const allChatHistoryCache = useSelector((state) => state.allChatHistoryRed);
  const allChatHistory = allChatHistoryCache.data || [];
  const [isLoading, setIsLoading] = useState(!allChatHistoryCache.fetched);
  const [loadingMessage, setLoadingMessage] = useState('Fetching chat history...');
  const hasFetchedRef = useRef(false);

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    (async () => {
      try {
        setIsLoading(true);
        setLoadingMessage('Fetching chat history...');
        await dispatch(getAllChatHistoryCached());
      } catch (error) {
        console.error('Error fetching chat history', error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [dispatch]);

  // Yearly app usage statistics of users
  const renderChartYearly = () => {
    const [year, setYear] = useState(new Date().getFullYear()); // Set initial year to current year
    const [monthlyUserData, setMonthlyUserData] = useState(Array(12).fill(0)); // Monthly user data
    const [totalUsers, setTotalUsers] = useState(0); // Total user count
    const [yearRange, setYearRange] = useState([new Date().getFullYear()]); // Range of years
    
    // Function to derive user data from already-fetched chat history
    const fetchUserData = () => {
  const userArray = allChatHistory;
  const userCounts = Array(12).fill(0); // Placeholder for monthly user count
  const uniqueUserIdsPerMonth = Array.from({ length: 12 }, () => new Set()); // Array of Sets for each month
  
  let oldestYear = new Date().getFullYear(); // Start with the current year as the oldest
  
  if (Array.isArray(userArray)) {
    userArray.forEach((record) => {
      if (record.createdUTC) {
        const recordDate = new Date(record.createdUTC); 
        if (!isNaN(recordDate)) {
          // Get the oldest year from data
          oldestYear = Math.min(oldestYear, recordDate.getFullYear());

          const month = recordDate.getMonth(); // Get month (0 for Jan, 11 for Dec)

          // Check if the record's year matches the selected year
          if (recordDate.getFullYear() === year) {
            // Add userId to the Set for the corresponding month
            if (!uniqueUserIdsPerMonth[month].has(record.userId)) {
              uniqueUserIdsPerMonth[month].add(record.userId); // Add userId to the Set
              userCounts[month] += 1; // Increment the count for the corresponding month
            }
          }
        }
      }
    });
  }

  // Flatten the unique user IDs across all months
  const uniqueUserIds = new Set();
  uniqueUserIdsPerMonth.forEach(monthSet => {
    monthSet.forEach(userId => {
      uniqueUserIds.add(userId);
    });
  });

  // Update the year range from the oldest year to the current year
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let i = oldestYear; i <= currentYear; i++) {
    years.push(i);
  }

  setYearRange(years);
  setMonthlyUserData(userCounts); // Update the chart with monthly user data
  setTotalUsers(uniqueUserIds.size); // Update total users based on unique IDs
};
  
    // Call fetchUserData on mount and whenever the year changes or chat history loads
    useEffect(() => {
      fetchUserData();
    }, [year, allChatHistory]);
  
    const handleYearChange = (event) => {
      setYear(parseInt(event.target.value)); // Set the year from dropdown selection
    };
  
    const chartData = {
      labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
      datasets: [
        {
          data: monthlyUserData,
          borderColor: '#0053ED', 
          backgroundColor: '#0053ED',
          borderWidth: 1,
          barPercentage: 0.6,
        },
      ],
    };
    
    const maxUsers = Math.max(...monthlyUserData) + 1;

    const options = {
      responsive: true,
     maintainAspectRatio: true, 
      scales: {
        y: {
          beginAtZero: true,
          max: maxUsers,
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return value ; // Add percentage sign to y-axis labels
            },
          },
          grid: {
            display: false, // Disable the background grid
          },
          title: {
            display: true,
            text: 'Users Count',
            font: {
              size: 14,
              weight: 'bold',
            },
          },
        },
        x: {
          title: {
            display: true,
            text: 'Month',
            font: {
              size: 14,
              weight: 'bold',
            },
          },
          grid: {
            display: false, // Disable the background grid
            // drawBorder: false, 
          },
        },
      },
      plugins: {
        legend: {
          display: false, // Disable legend
        },
        datalabels: {
          display: true,
          color: 'black', // Text color for the value
          anchor: 'end',
          align: 'end',
          font: {
            weight: 'bold',
            size: 12,
          },
          formatter: function (value) {
            return value; // Display the actual value on top of the bars
          },
        },
      },
    };
  
    return (
      <div  className="card_dashboard">
        {/* Title and subtitle */}
        <span>
        <h3 className="chart-title">Yearly AI Chat usage statistics of users</h3>
        <p className="card-subtitle">Total number of users - {totalUsers} </p>
        </span>
        {/* Year dropdown */}
        <span
  style={{
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: '10px',
  }}
> 
  <select
    value={year}
    onChange={handleYearChange}
    style={{
      padding: '5px 10px',
      fontSize: '14px',
      borderRadius: '4px',
      border: '1px solid #ccc',
      backgroundColor: '#f8f8f8',
      color: '#333',
      cursor: 'pointer',
      transition: 'border-color 0.3s, background-color 0.3s',
    }}
    onFocus={(e) => {
      e.target.style.borderColor = '#007BFF'; // Change border color on focus
      e.target.style.backgroundColor = '#fff'; // Change background on focus

       // Add logic to adjust position dynamically
       const rect = e.target.getBoundingClientRect(); // Get the position of the dropdown
       const windowWidth = window.innerWidth;

       // Check if the dropdown is going beyond the right edge of the screen
       if (rect.right > windowWidth) {
         e.target.style.position = 'absolute'; // Set position to absolute
         e.target.style.right = '0'; // Align it to the right edge of its container
         e.target.style.left = 'auto'; // Avoid overflowing to the right
       }
    }}
    
    onBlur={(e) => {
      e.target.style.borderColor = '#ccc'; // Revert border color
      e.target.style.backgroundColor = '#f8f8f8'; // Revert background
    }}
  >
    {yearRange.map((yr) => (
      <option key={yr} value={yr}>
        {yr}
      </option>
    ))}
  </select>
</span>
  
        <Bar data={chartData} options={options} />
      </div>
    );
  };

const renderChartWeekly = () => {
  // Function to get the previous Monday (normalized to start of day)
  const getPreviousMonday = () => {
    const today = new Date();
    const day = today.getDay();
    const prevMonday = new Date(today);
    prevMonday.setDate(today.getDate() - ((day + 6) % 7));
    prevMonday.setHours(0, 0, 0, 0);
    return prevMonday;
  };

  // End of today (23:59:59.999) so today's records are included
  const getEndOfToday = () => {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const [startDate, setStartDate] = useState(getPreviousMonday());
  const [endDate, setEndDate] = useState(getEndOfToday());
  const [dailyUserData, setDailyUserData] = useState(Array(7).fill(0));
  const [totalWeeklyUsers, setTotalWeeklyUsers] = useState(0);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  

  const fetchUserData = () => {
    const userArray = allChatHistory;
    const userCounts = Array(7).fill(0);
    const uniqueUserIds = new Set();
    const dailyUserTracker = {}; // To track unique users per day

    userArray.forEach((record) => {
      if (record.createdUTC) {
        const recordDate = new Date(record.createdUTC);
        if (recordDate >= startDate && recordDate <= endDate) {
          const day = (recordDate.getDay() + 6) % 7; // Adjusting to start from Monday

          const uniqueKey = `${record.userId}-${recordDate.toDateString()}`;

          if (!dailyUserTracker[uniqueKey]) {
            dailyUserTracker[uniqueKey] = true;
            uniqueUserIds.add(record.userId);
            userCounts[day] += 1;
          }
        }
      }
    });

    setDailyUserData(userCounts);
    setTotalWeeklyUsers(uniqueUserIds.size);
  };

  useEffect(() => {
    fetchUserData();
  }, [startDate, endDate, allChatHistory]);

  useEffect(() => {
    if (startDate) {
      const newEndDate = new Date(startDate);
      newEndDate.setDate(newEndDate.getDate() + 6);
      newEndDate.setHours(23, 59, 59, 999);
      setEndDate(newEndDate);
    }
  }, [startDate]);

  const chartData = {
    labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    datasets: [
      {
        data: dailyUserData,
        borderColor: '#0053ED',
        backgroundColor: '#0053ED',
        borderWidth: 1,
        barPercentage: 0.3,
      },
    ],
  };

  const maxUsers = Math.max(...dailyUserData) + 1;

  const options = {
    responsive: true,
   maintainAspectRatio: true,
    scales: {
      y: {
        beginAtZero: true,
        max: maxUsers,
        ticks: {
          stepSize: 1,
          callback: function (value) {
            return value;
          },
        },
        grid: {
          display: false,
        },
        title: {
          display: true,
          text: 'Users Count',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
      },
      x: {
        title: {
          display: true,
          text: 'Day',
          font: {
            size: 14,
            weight: 'bold',
          },
        },
        grid: {
          display: false,
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'end',
        align: 'end',
        color: 'black',
        font: {
          weight: 'bold',
        },
      },
    },
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <div className="card_dashboard">
      <h3 className="chart-title">Weekly AI Chat usage statistics of users</h3>
      <p className="card-subtitle">Total number of users - {totalWeeklyUsers}</p>

      <div className="date-picker-container">
        <div className="calendar-icon" style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
          <FontAwesomeIcon
            icon={faCalendar}
            onClick={() => setIsDatePickerOpen((prev) => !prev)}
          />
          <DatePicker
            selected={startDate}
            onChange={(date) => {
              if (date) {
                const normalizedStart = new Date(date);
                normalizedStart.setHours(0, 0, 0, 0);
                setStartDate(normalizedStart);
                const newEndDate = new Date(normalizedStart);
                newEndDate.setDate(newEndDate.getDate() + 6);
                newEndDate.setHours(23, 59, 59, 999);
                setEndDate(newEndDate);
              } else {
                setStartDate(date);
              }
            }}
            dateFormat="yyyy/MM/dd"
            className="date-picker"
            popperPlacement="bottom-end"
            popperModifiers={{
              preventOverflow: {
                enabled: true,
                boundariesElement: 'viewport', // Prevent cutting off
              },
            }}
            popperClassName="custom-datepicker-popper"
            customInput={<input style={{ display: 'none' }} />}
            open={isDatePickerOpen}
            onClickOutside={() => setIsDatePickerOpen(false)}
            onCalendarClose={() => setIsDatePickerOpen(false)}
            filterDate={(date) => date.getDay() === 1}
            placeholderText="Select Start Date"
          />
        </div>
        <span>{formatDate(startDate)}</span>
        <span className="date-separator">-</span>
        <span>{formatDate(endDate)}</span>
      </div>

      <Bar data={chartData} options={options} />
    </div>
  );
};

 const renderExperienceRation = () => {
  // States to store dynamic data (Hooks should always be at the top level, outside any loops or conditions)
  const [ratingsData, setRatingsData] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [averageRating, setAverageRating] = useState(0);
  const [totalDatasets, setTotalDatasets] = useState(0);
  const [totalRatedChats, setTotalRatedChats] = useState(0);
  const [totalChats, setTotalChats] = useState(0);
  // Function to fetch data
  const fetchRatingsData = async () => {
    try {
      // Fetch ratings data only; reuse already-loaded chat history
      const ratingResponse = await fetchAllStarRatings();
  
      const ratingData = ratingResponse.data; // Assume rating data is in payload
      const chatHistoryData = allChatHistory;
      setTotalChats(chatHistoryData.length);
      // 2. Extract chatIds, datasets and ratings
      const chatIds = new Set(ratingData.map(item => item.chatId)); // Use Set for faster lookup
      const ratings = ratingData.map(item => item.rating);
      const datasets = new Set(ratingData.map(item => item.datasetId));
      
      // calculate total number of datasets
      const totalDatasets = datasets.size;
      setTotalDatasets(totalDatasets);

      // 3. Map chatId to userId using chatHistoryData
      const userIds = chatHistoryData
        .filter(record => chatIds.has(record.id)) // Use Set for filtering
        .map(record => record.userId);
  
      // 4. Calculate unique users
      const uniqueUserIds = [...new Set(userIds)];
      setTotalUsers(uniqueUserIds.length);
  
      // 5. Calculate average rating
      const totalRatings = ratings.reduce((sum, rating) => sum + rating, 0);
      const avgRating = ratings.length > 0 ? totalRatings / ratings.length : 0; // Handle division by zero
      setAverageRating(avgRating);
      setTotalRatedChats(ratings.length);

      setTotalRatedChats(ratings.length);

  
      // 6. Group and process ratings for breakdown display
      const ratingCounts = ratings.reduce((acc, rating) => {
        acc[rating] = (acc[rating] || 0) + 1; // Count each rating
        return acc;
      }, {});
  
      const ratingBreakdown = [1, 2, 3, 4, 5].map(star => ({
        label: `${star} star`,
        percentage: calcPercentage(ratings, star),
        count: ratingCounts[star] || 0, // Use counted values
      }));
  
      setRatingsData(ratingBreakdown);
    } catch (error) {
      console.error('Error fetching ratings data:', error);
    }
  };
  
  
  
  

  // Helper functions to calculate percentages and counts
  const calcPercentage = (ratings, star) => {
    const count = ratings.filter(rating => rating === star).length;
    return (count / ratings.length) * 100;
  };

  // useEffect to fetch data on component mount or when chat history loads
  useEffect(() => {
    fetchRatingsData(); // Call API when component mounts or chat history changes
  }, [allChatHistory]); // Re-run when underlying data is ready

  return (
    <div className="card_dashboard">
      {/* Title and total rating */}
      <h3 className="chart-title">User Experience </h3>
      <p className="card-subtitle">{totalUsers} users rated {totalRatedChats} chats out of {totalChats} in {totalDatasets} datasets</p>
      

      {/* Star Rating Section */}
      <div className="star-rating-dashboard">
        <span className="stars">
          {/* Dynamically render stars based on average rating */}
          {'★'.repeat(Math.floor(averageRating))}
          {'☆'.repeat(5 - Math.floor(averageRating))}
        </span>
        <span className="rating-score">{averageRating.toFixed(1)} out of 5</span>
      </div>

      {/* Ratings Breakdown */}
      <div className="ratings-breakdown">
        {ratingsData.map((rating) => (
          <div className="rating-row" key={rating.label}>
            <span className="rating-label">{rating.label}</span>
            <div className="rating-bar">
              <div
                className="rating-bar-filled"
                style={{ width: `${rating.count > 0 ? rating.percentage : 0}%`, backgroundColor: '#FFA500' }}
                title={`Rating Percentage: ${rating.percentage.toFixed(1)}%`}
              ></div>
            </div>
            <span className="rating-percentage" style={{ marginRight: '10px' }}>
  <span className="label"> </span>
  <span title={`Rating Percentage: ${rating.percentage.toFixed(1)}%`}>
  {rating.count > 0 ? rating.percentage.toFixed(1) : '0.0'}%
  </span>
</span>
<span className="rating-count">
  <span className="label"> </span>
  <span title={`No of ratings: ${rating.count}`}>
    ({rating.count})
  </span>
</span>
          </div>
        ))}
      </div>
    </div>
  );
};

  return (
    <div className="dashboard" style={{ position: 'relative', minHeight: '300px' }}>
      {isLoading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <DashLoader />
        </div>
      )}
      <div class="dashboard-cards">
        <div className="chart-container">  
          {renderChartWeekly()}
        </div>
      </div>
      <div class="dashboard-cards">
        <div className="chart-container">
          {renderChartYearly()}
        </div>
      </div>
      <div class="dashboard-cards">
        <div className="chart-container" style={{ paddingBottom: '70px' }}>
          {renderExperienceRation()}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
